import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuditLogs() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get current user
        const userResponse = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (!userResponse.ok) {
          navigate('/login');
          return;
        }
        const userData = await userResponse.json();
        setUser(userData);

        // Check if user has permission (super_admin only)
        if (userData.role !== 'super_admin') {
          toast.error('You do not have permission to view audit logs');
          navigate('/');
          return;
        }

        // Fetch audit logs
        const logsResponse = await fetch(`${BACKEND_URL}/api/audit-logs?limit=50`, {
          credentials: 'include'
        });
        if (logsResponse.ok) {
          const logsData = await logsResponse.json();
          setLogs(logsData);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load audit logs');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8" data-testid="audit-logs-container">
        {/* Header */}
        <div className="mb-8 flex items-center gap-4">
          <Button
            data-testid="back-button"
            onClick={() => navigate('/')}
            variant="ghost"
            className="h-12 px-4"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-['Chivo'] font-bold text-4xl sm:text-5xl text-slate-900">
              Audit Logs
            </h1>
            <p className="text-slate-500 text-lg mt-1">
              System activity and user actions
            </p>
          </div>
        </div>

        {/* Logs List */}
        <Card className="bg-white border border-slate-200 rounded-lg shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left p-4 text-sm font-semibold text-slate-700">Timestamp</th>
                  <th className="text-left p-4 text-sm font-semibold text-slate-700">User ID</th>
                  <th className="text-left p-4 text-sm font-semibold text-slate-700">Action</th>
                  <th className="text-left p-4 text-sm font-semibold text-slate-700">Entity</th>
                  <th className="text-left p-4 text-sm font-semibold text-slate-700">Entity ID</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center">
                      <AlertCircle className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No audit logs found</p>
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.log_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid="audit-log-row">
                      <td className="p-4 text-sm text-slate-600">
                        {new Date(log.timestamp).toLocaleString('en-AU')}
                      </td>
                      <td className="p-4 text-sm text-slate-600 font-mono">
                        {log.user_id.substring(0, 12)}...
                      </td>
                      <td className="p-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {log.action}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-slate-600">
                        {log.entity}
                      </td>
                      <td className="p-4 text-sm text-slate-600 font-mono">
                        {log.entity_id ? `${log.entity_id.substring(0, 12)}...` : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Layout>
  );
}